import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Eye, Palette, Plus, Sparkles, Trash2, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  defaultPdfTheme,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  TENANT_ROLES,
  type ApiKeyDto,
  type AuditEventDto,
  type Paginated,
  type PdfThemeConfig,
  type PdfThemeDto,
  type TenantRole,
  type TenantSettings,
} from '@imsenta/shared';
import { PageHeader } from '@/components/AppShell';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Tabs,
  Textarea,
  useToast,
} from '@/components/ui';
import { PortalSettings } from '@/components/PortalSettings';
import { SsoSettings } from '@/components/SsoSettings';
import { api, ApiError, openPost } from '@/lib/api';
import { fmtDate, fmtDateTime, fmtRelative } from '@/lib/format';
import { useSession } from '@/lib/session';

type Tab = 'organisation' | 'members' | 'sso' | 'portal' | 'governance' | 'designs' | 'ai' | 'api' | 'audit' | 'profile';

interface TenantResponse {
  id: string;
  slug: string;
  name: string;
  settings: TenantSettings;
  role: TenantRole;
}

export function SettingsPage() {
  const { tab = 'organisation' } = useParams<{ tab?: Tab }>();
  const navigate = useNavigate();
  const { can } = useSession();

  const tabs = [
    { id: 'organisation' as Tab, label: 'Organisation' },
    { id: 'members' as Tab, label: 'Members' },
    ...(can('tenant:manage') ? [{ id: 'sso' as Tab, label: 'Single sign-on' }] : []),
    { id: 'portal' as Tab, label: 'Catalog portal' },
    { id: 'governance' as Tab, label: 'Governance' },
    { id: 'designs' as Tab, label: 'PDF designs' },
    { id: 'ai' as Tab, label: 'AI assistance' },
    ...(can('settings:manage') ? [{ id: 'api' as Tab, label: 'API' }] : []),
    ...(can('audit:read') ? [{ id: 'audit' as Tab, label: 'Audit log' }] : []),
    { id: 'profile' as Tab, label: 'Your profile' },
  ];

  return (
    <>
      <PageHeader title="Settings" description="Organisation, people, governance rules and document design." />
      <Tabs className="mb-5" value={tab as Tab} onChange={(next) => navigate(`/settings/${next}`)} tabs={tabs} />

      {tab === 'organisation' ? <OrganisationTab /> : null}
      {tab === 'members' ? <MembersTab /> : null}
      {tab === 'sso' ? <SsoSettings /> : null}
      {tab === 'portal' ? <PortalSettings /> : null}
      {tab === 'governance' ? <GovernanceTab /> : null}
      {tab === 'designs' ? <DesignsTab /> : null}
      {tab === 'ai' ? <AiTab /> : null}
      {tab === 'api' ? <ApiTab /> : null}
      {tab === 'audit' ? <AuditTab /> : null}
      {tab === 'profile' ? <ProfileTab /> : null}
    </>
  );
}

function useTenant() {
  return useQuery({ queryKey: ['tenant'], queryFn: () => api.get<TenantResponse>('/tenant') });
}

function OrganisationTab() {
  const { data, isLoading } = useTenant();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const { can, refresh } = useSession();
  const [name, setName] = useState('');
  const [settings, setSettings] = useState<Partial<TenantSettings>>({});

  useEffect(() => {
    if (data) {
      setName(data.name);
      setSettings(data.settings);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.patch('/tenant', {
        name,
        settings: {
          referencePadding: settings.referencePadding,
          defaultClassification: settings.defaultClassification,
          reviewIntervalMonths: settings.reviewIntervalMonths,
          requireChangeRequestForPublished: settings.requireChangeRequestForPublished,
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tenant'] });
      await refresh();
      push('Settings saved', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not save', 'danger'),
  });

  if (isLoading || !data) return <Spinner />;
  const readOnly = !can('tenant:manage');

  return (
    <div className="grid gap-5 lg:grid-cols-2 [&>*]:min-w-0">
      <Card>
        <CardHeader title="Organisation" />
        <div className="space-y-4 px-5 py-4">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={readOnly} />
          </Field>
          <Field label="Address" hint="Used in invitation links. This cannot be changed after sign-up.">
            <Input value={data.slug} disabled />
          </Field>
        </div>
      </Card>

      <Card>
        <CardHeader title="Document defaults" />
        <div className="space-y-4 px-5 py-4">
          <Field label="Reference number width" hint={`Documents are numbered like POL-${'0'.repeat(Math.max(0, (settings.referencePadding ?? 3) - 1))}1.`}>
            <Select
              value={String(settings.referencePadding ?? 3)}
              disabled={readOnly}
              onChange={(e) => setSettings({ ...settings, referencePadding: Number(e.target.value) })}
            >
              {[2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n} digits
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Default classification">
            <Select
              value={settings.defaultClassification ?? 'internal'}
              disabled={readOnly}
              onChange={(e) => setSettings({ ...settings, defaultClassification: e.target.value as TenantSettings['defaultClassification'] })}
            >
              {['public', 'internal', 'confidential', 'restricted'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Review interval" hint="How far ahead the next review date is set when a document is published.">
            <Select
              value={String(settings.reviewIntervalMonths ?? 12)}
              disabled={readOnly}
              onChange={(e) => setSettings({ ...settings, reviewIntervalMonths: Number(e.target.value) })}
            >
              {[6, 12, 18, 24, 36].map((n) => (
                <option key={n} value={n}>
                  {n} months
                </option>
              ))}
            </Select>
          </Field>

          <Checkbox
            label="Require a change request to edit a published document"
            hint="Authors must propose changes through governance. Administrators can still edit directly."
            checked={settings.requireChangeRequestForPublished ?? true}
            disabled={readOnly}
            onChange={(e) => setSettings({ ...settings, requireChangeRequestForPublished: e.target.checked })}
          />
        </div>
        {!readOnly ? (
          <div className="flex justify-end border-t border-ink-200 px-5 py-3">
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Save changes
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function MembersTab() {
  const { can, session } = useSession();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['members'],
    queryFn: () => api.get<{ items: { id: string; name: string; email: string; role: TenantRole; joinedAt: string; lastLoginAt: string | null }[] }>('/tenant/members'),
  });

  const { data: invitations } = useQuery({
    queryKey: ['invitations'],
    queryFn: () => api.get<{ items: { id: string; email: string; role: string; expiresAt: string; expired: boolean }[] }>('/tenant/invitations'),
    enabled: can('member:manage'),
    retry: false,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['members'] });
    queryClient.invalidateQueries({ queryKey: ['invitations'] });
  };

  const changeRole = useMutation({
    mutationFn: (input: { userId: string; role: TenantRole }) => api.patch(`/tenant/members/${input.userId}`, { role: input.role }),
    onSuccess: () => {
      invalidate();
      push('Role updated', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not change the role', 'danger'),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => api.del(`/tenant/members/${userId}`),
    onSuccess: () => {
      invalidate();
      setRemoving(null);
      push('Member removed', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not remove the member', 'danger'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/tenant/invitations/${id}`),
    onSuccess: () => {
      invalidate();
      push('Invitation withdrawn', 'success');
    },
  });

  if (isLoading) return <Spinner />;

  return (
    <>
      <div className="space-y-5">
        <Card>
          <CardHeader
            title="Members"
            description={`${data?.items.length ?? 0} people can access this organisation.`}
            actions={
              can('member:manage') ? (
                <Button variant="primary" icon={<UserPlus className="size-4" />} onClick={() => setInviteOpen(true)}>
                  Invite someone
                </Button>
              ) : null
            }
          />
          <ul className="divide-y divide-ink-100">
            {data?.items.map((member) => (
              <li key={member.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3">
                <Avatar name={member.name} email={member.email} size={32} />
                <div className="min-w-0 flex-1 basis-44">
                  <p className="text-[13.5px] font-medium text-ink-800">
                    {member.name}
                    {member.id === session?.user.id ? <span className="ml-1.5 text-[11.5px] text-ink-400">(you)</span> : null}
                  </p>
                  <p className="truncate text-[12px] text-ink-500">{member.email}</p>
                </div>
                <span className="hidden text-[12px] text-ink-400 sm:block">
                  {member.lastLoginAt ? `Active ${fmtRelative(member.lastLoginAt)}` : 'Never signed in'}
                </span>
                {can('member:manage') && member.id !== session?.user.id ? (
                  <>
                    <Select
                      value={member.role}
                      className="w-auto"
                      onChange={(e) => changeRole.mutate({ userId: member.id, role: e.target.value as TenantRole })}
                    >
                      {TENANT_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {ROLE_LABELS[role]}
                        </option>
                      ))}
                    </Select>
                    <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} onClick={() => setRemoving({ id: member.id, name: member.name })} aria-label="Remove" />
                  </>
                ) : (
                  <Badge tone="neutral">{ROLE_LABELS[member.role]}</Badge>
                )}
              </li>
            ))}
          </ul>
        </Card>

        {can('member:manage') && invitations?.items.length ? (
          <Card>
            <CardHeader title="Pending invitations" />
            <ul className="divide-y divide-ink-100">
              {invitations.items.map((invite) => (
                <li key={invite.id} className="flex items-center gap-3 px-5 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] text-ink-800">{invite.email}</p>
                    <p className="text-[11.5px] text-ink-500">
                      {ROLE_LABELS[invite.role as TenantRole]} · {invite.expired ? 'expired' : `expires ${fmtRelative(invite.expiresAt)}`}
                    </p>
                  </div>
                  <Button size="sm" onClick={() => revoke.mutate(invite.id)}>
                    Withdraw
                  </Button>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        <Card>
          <CardHeader title="What each role can do" />
          <ul className="divide-y divide-ink-100">
            {TENANT_ROLES.map((role) => (
              <li key={role} className="flex gap-3 px-5 py-2.5">
                <Badge tone="neutral" className="mt-0.5 shrink-0">
                  {ROLE_LABELS[role]}
                </Badge>
                <p className="text-[13px] text-ink-600">{ROLE_DESCRIPTIONS[role]}</p>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} onInvited={invalidate} />

      <ConfirmDialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
        title={`Remove ${removing?.name ?? 'this member'}?`}
        message="They will lose access to this organisation immediately. Documents they authored are kept."
        confirmLabel="Remove"
        loading={remove.isPending}
      />
    </>
  );
}

function InviteModal({ open, onClose, onInvited }: { open: boolean; onClose: () => void; onInvited: () => void }) {
  const { push } = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TenantRole>('author');
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () => api.post<{ inviteUrl: string }>('/tenant/members/invite', { email, role }),
    onSuccess: (result) => {
      setLink(result.inviteUrl);
      setEmail('');
      onInvited();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not create the invitation'),
  });

  return (
    <Modal
      open={open}
      onClose={() => {
        setLink(null);
        setError(null);
        onClose();
      }}
      title="Invite someone"
      description="This server does not send email, so pass the link on yourself."
      footer={
        <>
          <Button
            onClick={() => {
              setLink(null);
              setError(null);
              onClose();
            }}
          >
            {link ? 'Done' : 'Cancel'}
          </Button>
          {!link ? (
            <Button variant="primary" loading={invite.isPending} disabled={!email.trim()} onClick={() => invite.mutate()}>
              Create invitation
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}

        {link ? (
          <>
            <Alert tone="success" title="Invitation created">
              Send this link to the person you invited. It can be used once, and expires in 14 days.
            </Alert>
            <div className="flex gap-2">
              <Input value={link} readOnly className="font-mono text-[12px]" onFocus={(e) => e.currentTarget.select()} />
              <Button
                icon={<Copy className="size-4" />}
                onClick={() => {
                  void navigator.clipboard.writeText(link);
                  push('Link copied', 'success');
                }}
              >
                Copy
              </Button>
            </div>
            <Button onClick={() => setLink(null)}>Invite someone else</Button>
          </>
        ) : (
          <>
            <Field label="Email address" required>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="colleague@organisation.com" autoFocus />
            </Field>
            <Field label="Role" hint={ROLE_DESCRIPTIONS[role]}>
              <Select value={role} onChange={(e) => setRole(e.target.value as TenantRole)}>
                {TENANT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}
      </div>
    </Modal>
  );
}

interface PolicyRow {
  id: string;
  documentTypeKey: string | null;
  documentTypeName: string | null;
  minApprovals: number;
  requiredRoles: TenantRole[];
  allowSelfApproval: boolean;
  blockOnRequestedChanges: boolean;
}

function GovernanceTab() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [editing, setEditing] = useState<PolicyRow | 'new' | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['approval-policies'], queryFn: () => api.get<{ items: PolicyRow[] }>('/approval-policies') });
  const { data: types } = useQuery({ queryKey: ['document-types'], queryFn: () => api.get<{ items: { key: string; name: string }[] }>('/document-types') });

  if (isLoading) return <Spinner />;

  const defaultPolicy = data?.items.find((p) => !p.documentTypeKey);
  const specific = data?.items.filter((p) => p.documentTypeKey) ?? [];

  return (
    <>
      <div className="space-y-5">
        <Alert tone="info" title="How approval works">
          A change request can only be merged once it has enough approvals from the roles you name here. The author of a proposal
          never counts towards its own approvals unless you allow it.
        </Alert>

        <Card>
          <CardHeader
            title="Default approval policy"
            description="Applies to every document type without a rule of its own."
            actions={can('settings:manage') && defaultPolicy ? <Button size="sm" onClick={() => setEditing(defaultPolicy)}>Edit</Button> : null}
          />
          {defaultPolicy ? <PolicySummary policy={defaultPolicy} /> : <EmptyState title="No default policy set" />}
        </Card>

        <Card>
          <CardHeader
            title="Per-type policies"
            description="Override the default for a specific kind of document, e.g. require two approvals for standards."
            actions={can('settings:manage') ? <Button size="sm" variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>Add rule</Button> : null}
          />
          {specific.length === 0 ? (
            <EmptyState title="No overrides" description="Every document type uses the default policy." />
          ) : (
            <ul className="divide-y divide-ink-100">
              {specific.map((policy) => (
                <li key={policy.id}>
                  <div className="flex items-center justify-between gap-3 px-5 pt-3">
                    <p className="text-[13.5px] font-semibold text-ink-800">{policy.documentTypeName ?? policy.documentTypeKey}</p>
                    {can('settings:manage') ? (
                      <Button size="sm" onClick={() => setEditing(policy)}>
                        Edit
                      </Button>
                    ) : null}
                  </div>
                  <PolicySummary policy={policy} />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {editing ? (
        <PolicyModal
          policy={editing === 'new' ? null : editing}
          types={types?.items ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['approval-policies'] });
            setEditing(null);
            push('Approval policy saved', 'success');
          }}
        />
      ) : null}
    </>
  );
}

function PolicySummary({ policy }: { policy: PolicyRow }) {
  return (
    <dl className="grid gap-x-6 gap-y-2 px-5 py-3 text-[13px] sm:grid-cols-2">
      <div className="flex justify-between gap-3">
        <dt className="text-ink-500">Approvals required</dt>
        <dd className="font-medium text-ink-800">{policy.minApprovals}</dd>
      </div>
      <div className="flex justify-between gap-3">
        <dt className="text-ink-500">Must include</dt>
        <dd className="font-medium text-ink-800">{policy.requiredRoles.map((r) => ROLE_LABELS[r]).join(' or ') || 'anyone'}</dd>
      </div>
      <div className="flex justify-between gap-3">
        <dt className="text-ink-500">Author may self-approve</dt>
        <dd className="font-medium text-ink-800">{policy.allowSelfApproval ? 'Yes' : 'No'}</dd>
      </div>
      <div className="flex justify-between gap-3">
        <dt className="text-ink-500">Requested changes block merge</dt>
        <dd className="font-medium text-ink-800">{policy.blockOnRequestedChanges ? 'Yes' : 'No'}</dd>
      </div>
    </dl>
  );
}

function PolicyModal({
  policy,
  types,
  onClose,
  onSaved,
}: {
  policy: PolicyRow | null;
  types: { key: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [documentTypeKey, setDocumentTypeKey] = useState(policy?.documentTypeKey ?? '');
  const [minApprovals, setMinApprovals] = useState(policy?.minApprovals ?? 1);
  const [requiredRoles, setRequiredRoles] = useState<TenantRole[]>(policy?.requiredRoles ?? ['admin']);
  const [allowSelfApproval, setAllowSelfApproval] = useState(policy?.allowSelfApproval ?? false);
  const [blockOnRequestedChanges, setBlockOnRequestedChanges] = useState(policy?.blockOnRequestedChanges ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.put('/approval-policies', {
        documentTypeKey: documentTypeKey || null,
        minApprovals,
        requiredRoles,
        allowSelfApproval,
        blockOnRequestedChanges,
      }),
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save the policy'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={policy?.documentTypeKey ? `Policy for ${policy.documentTypeName}` : policy ? 'Default approval policy' : 'New approval rule'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Save policy
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}

        {!policy || policy.documentTypeKey ? (
          <Field label="Applies to" hint="Leave as “All document types” to change the default.">
            <Select value={documentTypeKey} onChange={(e) => setDocumentTypeKey(e.target.value)} disabled={Boolean(policy)}>
              <option value="">All document types (default)</option>
              {types.map((type) => (
                <option key={type.key} value={type.key}>
                  {type.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        <Field label="Approvals required">
          <Select value={String(minApprovals)} onChange={(e) => setMinApprovals(Number(e.target.value))}>
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n === 0 ? 'None — anyone with merge rights may proceed' : `${n} approval${n === 1 ? '' : 's'}`}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="At least one approval must come from" hint="A more senior role also satisfies the requirement.">
          <div className="space-y-1.5 rounded-lg border border-ink-200 p-3">
            {TENANT_ROLES.filter((r) => r !== 'reader').map((role) => (
              <Checkbox
                key={role}
                label={ROLE_LABELS[role]}
                checked={requiredRoles.includes(role)}
                onChange={(e) => setRequiredRoles((current) => (e.target.checked ? [...current, role] : current.filter((r) => r !== role)))}
              />
            ))}
          </div>
        </Field>

        <Checkbox
          label="Allow an author to approve their own change request"
          hint="Off is recommended: separation of duties is a common audit expectation."
          checked={allowSelfApproval}
          onChange={(e) => setAllowSelfApproval(e.target.checked)}
        />

        <Checkbox
          label="Block merging while any reviewer has requested changes"
          checked={blockOnRequestedChanges}
          onChange={(e) => setBlockOnRequestedChanges(e.target.checked)}
        />
      </div>
    </Modal>
  );
}

function DesignsTab() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [editing, setEditing] = useState<PdfThemeDto | 'new' | null>(null);
  const [deleting, setDeleting] = useState<PdfThemeDto | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['pdf-themes'], queryFn: () => api.get<{ items: PdfThemeDto[] }>('/pdf-themes') });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['pdf-themes'] });

  const setDefault = useMutation({
    mutationFn: (id: string) => api.post(`/pdf-themes/${id}/default`),
    onSuccess: () => {
      invalidate();
      push('Default design updated', 'success');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/pdf-themes/${id}`),
    onSuccess: () => {
      invalidate();
      setDeleting(null);
      push('Design deleted', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not delete', 'danger'),
  });

  if (isLoading) return <Spinner />;

  return (
    <>
      <Card>
        <CardHeader
          title="PDF designs"
          description="Cover page, typography, colours and page furniture for exported documents."
          actions={
            can('settings:manage') ? (
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>
                New design
              </Button>
            ) : null
          }
        />
        <ul className="divide-y divide-ink-100">
          {data?.items.map((theme) => (
            <li key={theme.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3.5">
              <span className="size-9 shrink-0 rounded-md ring-1 ring-ink-200 ring-inset" style={{ background: theme.config.colors.accent }} />
              <div className="min-w-0 flex-1 basis-44">
                <p className="flex items-center gap-2 text-[13.5px] font-medium text-ink-800">
                  {theme.name}
                  {theme.isDefault ? <Badge tone="brand">default</Badge> : null}
                </p>
                <p className="truncate text-[12px] text-ink-500">
                  {theme.description ?? `${theme.config.page.size} ${theme.config.page.orientation}`}
                  {theme.config.cover.enabled ? ' · cover page' : ''}
                  {theme.config.toc.enabled ? ' · contents' : ''}
                </p>
              </div>
              {can('settings:manage') ? (
                <>
                  {!theme.isDefault ? (
                    <Button size="sm" onClick={() => setDefault.mutate(theme.id)}>
                      Make default
                    </Button>
                  ) : null}
                  <Button size="sm" variant="primary" icon={<Palette className="size-3.5" />} onClick={() => setEditing(theme)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} onClick={() => setDeleting(theme)} aria-label="Delete" />
                </>
              ) : null}
            </li>
          ))}
        </ul>
      </Card>

      {editing ? (
        <ThemeModal
          theme={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            invalidate();
            setEditing(null);
            push('Design saved', 'success');
          }}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        title={`Delete “${deleting?.name}”?`}
        message="Documents exported with this design keep their existing files; future exports will use the default."
        confirmLabel="Delete"
        loading={remove.isPending}
      />
    </>
  );
}

function ThemeModal({ theme, onClose, onSaved }: { theme: PdfThemeDto | null; onClose: () => void; onSaved: () => void }) {
  const { push } = useToast();
  const [name, setName] = useState(theme?.name ?? '');
  const [description, setDescription] = useState(theme?.description ?? '');
  const [config, setConfig] = useState<PdfThemeConfig>(theme?.config ?? defaultPdfTheme());
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => () => void (previewUrl && URL.revokeObjectURL(previewUrl)), [previewUrl]);

  const patch = <K extends keyof PdfThemeConfig>(key: K, value: Partial<PdfThemeConfig[K]>) =>
    setConfig((current) => ({ ...current, [key]: { ...(current[key] as object), ...value } }));

  const save = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), description: description || undefined, config };
      return theme ? api.put(`/pdf-themes/${theme.id}`, payload) : api.post('/pdf-themes', payload);
    },
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save the design'),
  });

  const preview = useMutation({
    mutationFn: () => openPost('/pdf-themes/preview', { config }),
    onSuccess: (url) => {
      setPreviewUrl(url);
      window.open(url, '_blank', 'noopener');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not build a preview', 'danger'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      width="xl"
      title={theme ? `Edit “${theme.name}”` : 'New PDF design'}
      description="Placeholders such as {{reference}}, {{version}} and {{page}} are filled in at export time."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button icon={<Eye className="size-4" />} loading={preview.isPending} onClick={() => preview.mutate()}>
            Preview PDF
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={!name.trim()} onClick={() => save.mutate()}>
            Save design
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {error ? <Alert tone="danger">{error}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Design name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Corporate" />
          </Field>
          <Field label="Description">
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
        </div>

        <Section title="Page">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Size">
              <Select value={config.page.size} onChange={(e) => patch('page', { size: e.target.value as PdfThemeConfig['page']['size'] })}>
                {['A4', 'LETTER', 'LEGAL', 'A3'].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Orientation">
              <Select value={config.page.orientation} onChange={(e) => patch('page', { orientation: e.target.value as 'portrait' | 'landscape' })}>
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </Select>
            </Field>
            <Field label="Margin (pt)">
              <Input
                type="number"
                min={0}
                max={200}
                value={config.page.margins.top}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  patch('page', { margins: { top: n, bottom: n, left: Math.round(n * 0.9), right: Math.round(n * 0.9) } });
                }}
              />
            </Field>
          </div>
        </Section>

        <Section title="Typography">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Body font">
              <Select value={config.typography.bodyFont} onChange={(e) => patch('typography', { bodyFont: e.target.value as PdfThemeConfig['typography']['bodyFont'] })}>
                {['Roboto', 'Times', 'Helvetica', 'Courier'].map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Heading font">
              <Select value={config.typography.headingFont} onChange={(e) => patch('typography', { headingFont: e.target.value as PdfThemeConfig['typography']['headingFont'] })}>
                {['Roboto', 'Times', 'Helvetica', 'Courier'].map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Base size (pt)">
              <Input
                type="number"
                min={6}
                max={18}
                step={0.5}
                value={config.typography.baseSize}
                onChange={(e) => patch('typography', { baseSize: Number(e.target.value) })}
              />
            </Field>
          </div>
        </Section>

        <Section title="Colours">
          <div className="grid gap-3 sm:grid-cols-4">
            {(
              [
                ['accent', 'Accent'],
                ['heading', 'Headings'],
                ['text', 'Body text'],
                ['rule', 'Rules'],
              ] as const
            ).map(([key, label]) => (
              <Field key={key} label={label}>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={config.colors[key]}
                    onChange={(e) => patch('colors', { [key]: e.target.value } as Partial<PdfThemeConfig['colors']>)}
                    className="h-9 w-10 cursor-pointer rounded border border-ink-200 bg-white p-0.5"
                  />
                  <Input
                    value={config.colors[key]}
                    onChange={(e) => patch('colors', { [key]: e.target.value } as Partial<PdfThemeConfig['colors']>)}
                    className="font-mono text-[12px]"
                  />
                </div>
              </Field>
            ))}
          </div>
        </Section>

        <Section title="Cover page">
          <div className="space-y-3">
            <Checkbox label="Include a cover page" checked={config.cover.enabled} onChange={(e) => patch('cover', { enabled: e.target.checked })} />
            {config.cover.enabled ? (
              <>
                <Checkbox label="Show the metadata table" checked={config.cover.showMetadataTable} onChange={(e) => patch('cover', { showMetadataTable: e.target.checked })} />
                <Checkbox label="Show a classification banner" checked={config.cover.showClassificationBanner} onChange={(e) => patch('cover', { showClassificationBanner: e.target.checked })} />
                <Field label="Logo" hint="PNG, JPEG or SVG. Stored inline with the design.">
                  <div className="flex items-center gap-3">
                    {config.cover.logoDataUri ? (
                      <img src={config.cover.logoDataUri} alt="Logo preview" className="h-9 max-w-32 object-contain" />
                    ) : null}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                      className="text-[12.5px]"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 1_400_000) {
                          push('Please choose an image under about 1 MB', 'danger');
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = () => patch('cover', { logoDataUri: String(reader.result) });
                        reader.readAsDataURL(file);
                      }}
                    />
                    {config.cover.logoDataUri ? (
                      <Button size="sm" onClick={() => patch('cover', { logoDataUri: null })}>
                        Remove
                      </Button>
                    ) : null}
                  </div>
                </Field>
              </>
            ) : null}
          </div>
        </Section>

        <Section title="Contents and page furniture">
          <div className="space-y-3">
            <Checkbox label="Include a table of contents" checked={config.toc.enabled} onChange={(e) => patch('toc', { enabled: e.target.checked })} />
            <Checkbox label="Number the headings" checked={config.sections.numberHeadings} onChange={(e) => patch('sections', { numberHeadings: e.target.checked })} />
            <Checkbox label="Append the version history" checked={config.sections.showVersionHistory} onChange={(e) => patch('sections', { showVersionHistory: e.target.checked })} />
            <Checkbox label="Start each top-level section on a new page" checked={config.sections.startEachH1OnNewPage} onChange={(e) => patch('sections', { startEachH1OnNewPage: e.target.checked })} />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Header, left">
                <Input value={config.header.left} onChange={(e) => patch('header', { left: e.target.value })} className="font-mono text-[12px]" />
              </Field>
              <Field label="Header, right">
                <Input value={config.header.right} onChange={(e) => patch('header', { right: e.target.value })} className="font-mono text-[12px]" />
              </Field>
              <Field label="Footer, left">
                <Input value={config.footer.left} onChange={(e) => patch('footer', { left: e.target.value })} className="font-mono text-[12px]" />
              </Field>
              <Field label="Footer, right">
                <Input value={config.footer.right} onChange={(e) => patch('footer', { right: e.target.value })} className="font-mono text-[12px]" />
              </Field>
            </div>
          </div>
        </Section>

        <Section title="Watermark">
          <div className="space-y-3">
            <Checkbox label="Always watermark" checked={config.watermark.enabled} onChange={(e) => patch('watermark', { enabled: e.target.checked })} />
            <Checkbox
              label="Watermark unpublished versions automatically"
              hint="Marks drafts and proposals as such when they are exported."
              checked={config.watermark.autoForDrafts}
              onChange={(e) => patch('watermark', { autoForDrafts: e.target.checked })}
            />
            <Field label="Watermark text">
              <Input value={config.watermark.text} onChange={(e) => patch('watermark', { text: e.target.value })} />
            </Field>
          </div>
        </Section>
      </div>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-ink-200 p-4">
      <p className="mb-3 text-[11px] font-semibold tracking-wider text-ink-500 uppercase">{title}</p>
      {children}
    </div>
  );
}

function AiTab() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const { data } = useTenant();
  const { data: status } = useQuery({
    queryKey: ['ai-status'],
    queryFn: () => api.get<{ enabled: boolean; provider: string; model: string; reason: string | null }>('/ai/status'),
    retry: false,
  });

  const [form, setForm] = useState({ enabled: false, provider: 'openai', model: 'gpt-4o-mini', apiKey: '', baseUrl: '' });

  /*
   * Provider and model are null until this organisation overrides them, which
   * means "use whatever the server is set to". The form shows the value
   * actually in force, which /ai/status reports, so the field is never blank
   * and saving does not quietly change the behaviour.
   */
  useEffect(() => {
    if (data) {
      setForm({
        enabled: data.settings.ai.enabled,
        provider: data.settings.ai.provider ?? status?.provider ?? 'openai',
        model: data.settings.ai.model ?? status?.model ?? 'gpt-4o-mini',
        apiKey: '',
        baseUrl: data.settings.ai.baseUrl ?? '',
      });
    }
  }, [data, status]);

  const save = useMutation({
    mutationFn: () =>
      api.patch('/tenant', {
        settings: {
          ai: {
            enabled: form.enabled,
            provider: form.provider as 'openai' | 'azure-openai' | 'none',
            model: form.model,
            baseUrl: form.baseUrl || null,
            // An empty string clears the stored key; undefined leaves it alone.
            ...(form.apiKey ? { apiKey: form.apiKey } : {}),
          },
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      queryClient.invalidateQueries({ queryKey: ['ai-status'] });
      setForm((current) => ({ ...current, apiKey: '' }));
      push('AI settings saved', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not save', 'danger'),
  });

  const clearKey = useMutation({
    mutationFn: () => api.patch('/tenant', { settings: { ai: { apiKey: '' } } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      queryClient.invalidateQueries({ queryKey: ['ai-status'] });
      push('API key removed', 'success');
    },
  });

  if (!data) return <Spinner />;
  const readOnly = !can('settings:manage');

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px] [&>*]:min-w-0">
      <Card>
        <CardHeader title="AI drafting assistance" description="Optional. Authors can ask for a draft, a rewrite or a review of a section." />
        <div className="space-y-4 px-5 py-4">
          <Checkbox
            label="Enable AI assistance for this organisation"
            hint="Off by default. Nothing is sent to the provider unless an author asks for help."
            checked={form.enabled}
            disabled={readOnly}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />

          <Field label="Provider">
            <Select value={form.provider} disabled={readOnly} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
              <option value="openai">OpenAI</option>
              <option value="azure-openai">Azure OpenAI</option>
              <option value="none">None</option>
            </Select>
          </Field>

          <Field label="Model">
            <Input value={form.model} disabled={readOnly} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="gpt-4o-mini" />
          </Field>

          <Field
            label="API key"
            hint={data.settings.ai.apiKeySet ? 'A key is stored. Enter a new one to replace it — it is never shown again.' : 'Paste your provider API key.'}
          >
            <div className="flex gap-2">
              <Input
                type="password"
                value={form.apiKey}
                disabled={readOnly}
                onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                placeholder={data.settings.ai.apiKeySet ? '•••••••••••••••• (stored)' : 'sk-…'}
                autoComplete="off"
              />
              {data.settings.ai.apiKeySet && !readOnly ? (
                <Button onClick={() => clearKey.mutate()} loading={clearKey.isPending}>
                  Remove
                </Button>
              ) : null}
            </div>
          </Field>

          <Field label="Base URL" hint="Only needed for Azure OpenAI or a compatible gateway.">
            <Input value={form.baseUrl} disabled={readOnly} onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} placeholder="Optional" />
          </Field>
        </div>
        {!readOnly ? (
          <div className="flex justify-end border-t border-ink-200 px-5 py-3">
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Save AI settings
            </Button>
          </div>
        ) : null}
      </Card>

      <div className="space-y-4">
        <Card>
          <CardHeader title="Status" />
          <div className="px-5 py-4">
            {status?.enabled ? (
              <Alert tone="success" title="Available">
                Authors can use the assistant with <span className="font-mono">{status.model}</span>.
              </Alert>
            ) : (
              <Alert tone="warning" title="Not available">
                {status?.reason ?? 'AI assistance is switched off.'}
              </Alert>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="What to expect" />
          <ul className="space-y-2.5 px-5 py-4 text-[13px] text-ink-600">
            <li className="flex gap-2">
              <Sparkles className="mt-0.5 size-3.5 shrink-0 text-violet-500" />
              Suggestions are drafts. Nothing is written into a document until a person reads it and chooses to insert it.
            </li>
            <li className="flex gap-2">
              <Sparkles className="mt-0.5 size-3.5 shrink-0 text-violet-500" />
              The section being worked on, and sometimes the rest of the document, is sent to your provider. Do not enable this for
              content you cannot share with them.
            </li>
            <li className="flex gap-2">
              <Sparkles className="mt-0.5 size-3.5 shrink-0 text-violet-500" />
              Every request is recorded in the audit log with the action taken and the model used — never the content.
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
}

function AuditTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['audit', page],
    queryFn: () => api.get<Paginated<AuditEventDto>>('/tenant/audit', { page, pageSize: 50 }),
  });

  if (isLoading) return <Spinner />;

  return (
    <Card>
      <CardHeader title="Audit log" description="Every change to documents, governance and settings, with who made it." />
      {!data?.items.length ? (
        <EmptyState title="Nothing recorded yet" />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead className="border-b border-ink-200 bg-ink-50/60 text-[11px] tracking-wide text-ink-500 uppercase">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">When</th>
                  <th className="px-4 py-2.5 font-semibold">Who</th>
                  <th className="px-4 py-2.5 font-semibold">Action</th>
                  <th className="px-4 py-2.5 font-semibold">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {data.items.map((event) => (
                  <tr key={event.id}>
                    <td className="px-4 py-2 whitespace-nowrap text-ink-500">{fmtDateTime(event.createdAt)}</td>
                    <td className="px-4 py-2">
                      {event.actor ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Avatar name={event.actor.name} email={event.actor.email} size={18} />
                          <span className="text-ink-700">{event.actor.name}</span>
                        </span>
                      ) : (
                        <span className="text-ink-400">system</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span className="font-mono text-[12px] text-ink-700">{event.action}</span>
                    </td>
                    <td className="max-w-md px-4 py-2">
                      <span className="truncate text-[12px] text-ink-500">
                        {Object.entries(event.data)
                          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : String(v)}`)
                          .join(' · ') || '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.totalPages > 1 ? (
            <div className="flex items-center justify-between border-t border-ink-200 px-4 py-3">
              <p className="text-[12.5px] text-ink-500">
                Page {data.page} of {data.totalPages} · {data.total} events
              </p>
              <div className="flex gap-2">
                <Button size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <Button size="sm" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

function ProfileTab() {
  const { session, refresh, logout } = useSession();
  const { push } = useToast();
  const [name, setName] = useState(session?.user.name ?? '');
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [error, setError] = useState<string | null>(null);

  const saveProfile = useMutation({
    mutationFn: () => api.patch('/auth/profile', { name }),
    onSuccess: async () => {
      await refresh();
      push('Profile updated', 'success');
    },
  });

  const changePassword = useMutation({
    mutationFn: () => api.post('/auth/change-password', { currentPassword: passwords.current, newPassword: passwords.next }),
    onSuccess: () => {
      setPasswords({ current: '', next: '', confirm: '' });
      setError(null);
      push('Password changed. Your other sessions have been signed out.', 'success');
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not change your password'),
  });

  return (
    <div className="grid gap-5 lg:grid-cols-2 [&>*]:min-w-0">
      <Card>
        <CardHeader title="Your details" />
        <div className="space-y-4 px-5 py-4">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Email address" hint="Contact an administrator to change this.">
            <Input value={session?.user.email ?? ''} disabled />
          </Field>
        </div>
        <div className="flex justify-end border-t border-ink-200 px-5 py-3">
          <Button variant="primary" loading={saveProfile.isPending} onClick={() => saveProfile.mutate()}>
            Save
          </Button>
        </div>
      </Card>

      <Card>
        <CardHeader title="Password" description="Changing it signs out every other device." />
        <div className="space-y-4 px-5 py-4">
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <Field label="Current password">
            <Input type="password" value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} autoComplete="current-password" />
          </Field>
          <Field label="New password" hint="At least 12 characters, with upper case, lower case and a number.">
            <Input type="password" value={passwords.next} onChange={(e) => setPasswords({ ...passwords, next: e.target.value })} autoComplete="new-password" />
          </Field>
          <Field
            label="Confirm new password"
            error={passwords.confirm && passwords.confirm !== passwords.next ? 'The passwords do not match' : null}
          >
            <Input type="password" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} autoComplete="new-password" />
          </Field>
        </div>
        <div className="flex justify-between border-t border-ink-200 px-5 py-3">
          <Button variant="ghost" onClick={() => void logout()}>
            Sign out
          </Button>
          <Button
            variant="primary"
            loading={changePassword.isPending}
            disabled={!passwords.current || !passwords.next || passwords.next !== passwords.confirm}
            onClick={() => changePassword.mutate()}
          >
            Change password
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Textarea2() {
  return <Textarea />;
}
void Textarea2;

/**
 * API keys, and enough documentation to get somebody started without leaving
 * the page. The full reference is the OpenAPI document linked below.
 */
function ApiTab() {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['policies']);
  const [created, setCreated] = useState<{ token: string; name: string } | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get<{ items: ApiKeyDto[] }>('/tenant/api-keys'),
  });

  const create = useMutation({
    mutationFn: () => api.post<{ key: ApiKeyDto; token: string }>('/tenant/api-keys', { name: name.trim(), scopes }),
    onSuccess: (result) => {
      // Shown once, and only here: the server keeps a hash, not the token.
      setCreated({ token: result.token, name: result.key.name });
      setName('');
      setScopes(['policies']);
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not create the key', 'danger'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/tenant/api-keys/${id}`),
    onSuccess: () => {
      setRevoking(null);
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      push('Key revoked', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not revoke the key', 'danger'),
  });

  const base = `${window.location.origin}/api/public/v1`;
  const active = (data?.items ?? []).filter((key) => !key.revokedAt);
  const revoked = (data?.items ?? []).filter((key) => key.revokedAt);

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader
          title="Publish your policies elsewhere"
          description="A read-only API over the documents you have published, for an intranet, a wiki or a static site."
        />
        <div className="space-y-4 px-5 py-4 text-[13.5px] leading-relaxed text-ink-600">
          <p>
            Everything the API returns is already published to your people. Drafts, proposals and sections marked
            internal are never included, so a key that leaks exposes nothing that was not on the catalog already.
          </p>
          <div className="rounded-lg border border-ink-200 bg-ink-50 p-4">
            <p className="mb-2 text-[12.5px] font-semibold text-ink-700">Fetch your published policies</p>
            <pre className="overflow-x-auto text-[12px] leading-relaxed text-ink-800">
              <code>{`curl -H "Authorization: Bearer YOUR_KEY" \\\n  ${base}/documents`}</code>
            </pre>
          </div>
          <div className="rounded-lg border border-ink-200 bg-ink-50 p-4">
            <p className="mb-2 text-[12.5px] font-semibold text-ink-700">Feed your SIEM</p>
            <pre className="overflow-x-auto text-[12px] leading-relaxed text-ink-800">
              <code>{`# JSON Lines, oldest first. Keep the cursor and resume from it.\ncurl -sD- -H "Authorization: Bearer YOUR_KEY" \\\n  ${base}/events?limit=500`}</code>
            </pre>
            <p className="mt-2 text-[12px] text-ink-500">
              Needs a key with the audit scope. The next cursor comes back in the{' '}
              <code>x-imsenta-next-cursor</code> header.
            </p>
          </div>
          <div className="rounded-lg border border-ink-200 bg-ink-50 p-4">
            <p className="mb-2 text-[12.5px] font-semibold text-ink-700">Render one on an intranet page</p>
            <pre className="overflow-x-auto text-[12px] leading-relaxed text-ink-800">
              <code>{`curl -H "Authorization: Bearer YOUR_KEY" \\\n  ${base}/documents/POL-001\n\n# …or the file itself\ncurl -H "Authorization: Bearer YOUR_KEY" \\\n  ${base}/documents/POL-001.md`}</code>
            </pre>
          </div>
          <Alert tone="warning">
            Call the API from your server, never from a browser. Anything in front-end JavaScript can be read by whoever
            loads the page, and the key would go with it.
          </Alert>
          <p>
            The full reference is the{' '}
            <a href={`${base}/openapi.json`} className="font-medium text-brand-600 hover:underline" target="_blank" rel="noreferrer">
              OpenAPI description
            </a>
            , which needs no key to read.
          </p>
        </div>
      </Card>

      <Card>
        <CardHeader title="Keys" description="A key belongs to the organisation, not to a person. Revoking one affects nobody's sign-in." />
        <div className="space-y-4 px-5 py-4">
          {created ? (
            <Alert tone="success" title={`"${created.name}" created`}>
              <p className="mb-2">Copy it now. It is not shown again, and we keep only a hash of it.</p>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 overflow-x-auto rounded bg-white px-2.5 py-1.5 font-mono text-[12px] text-ink-800">
                  {created.token}
                </code>
                <Button
                  size="sm"
                  onClick={() => {
                    void navigator.clipboard?.writeText(created.token);
                    push('Copied', 'success');
                  }}
                >
                  Copy
                </Button>
              </div>
            </Alert>
          ) : null}

          <div className="space-y-3 rounded-lg border border-ink-200 p-4">
            <Field label="Name a new key" hint="Something you will recognise, such as “Intranet” or “Splunk”.">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Intranet" />
            </Field>

            <fieldset>
              <legend className="text-[13px] font-medium text-ink-800">What may it read?</legend>
              <div className="mt-2 space-y-2">
                {(
                  [
                    ['policies', 'Published policies', 'The documents already published to your people.'],
                    [
                      'audit',
                      'Audit and authentication log',
                      'Who did what, and who failed to sign in. Grant this only to your SIEM.',
                    ],
                  ] as const
                ).map(([id, label, description]) => (
                  <label key={id} className="flex cursor-pointer gap-2.5">
                    <input
                      type="checkbox"
                      checked={scopes.includes(id)}
                      onChange={(e) =>
                        setScopes((current) =>
                          e.target.checked ? [...current, id] : current.filter((scope) => scope !== id),
                        )
                      }
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-[13.5px] font-medium text-ink-800">{label}</span>
                      <span className="block text-[12.5px] text-ink-500">{description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <Button
              variant="primary"
              loading={create.isPending}
              disabled={!name.trim() || !scopes.length}
              onClick={() => create.mutate()}
            >
              Create key
            </Button>
          </div>

          {isLoading ? <Spinner /> : null}

          {active.length ? (
            <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
              {active.map((key) => (
                <li key={key.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13.5px] font-medium text-ink-900">{key.name}</p>
                    <p className="text-[12px] text-ink-500">
                      <code>imk_{key.prefix}…</code> · {key.scopes.join(', ')} · created {fmtDate(key.createdAt)} ·{' '}
                      {key.lastUsedAt ? `last used ${fmtRelative(key.lastUsedAt)}` : 'never used'}
                      {key.expiresAt ? ` · expires ${fmtDate(key.expiresAt)}` : ''}
                    </p>
                  </div>
                  <Button size="sm" variant="danger" onClick={() => setRevoking(key.id)}>
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[13px] text-ink-500">No keys yet.</p>
          )}

          {revoked.length ? (
            <details className="text-[13px] text-ink-500">
              <summary className="cursor-pointer">{revoked.length} revoked</summary>
              <ul className="mt-2 space-y-1">
                {revoked.map((key) => (
                  <li key={key.id}>
                    {key.name} · <code>imk_{key.prefix}…</code> · revoked {fmtDate(key.revokedAt!)}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      </Card>

      <ConfirmDialog
        open={Boolean(revoking)}
        onClose={() => setRevoking(null)}
        onConfirm={() => revoking && revoke.mutate(revoking)}
        title="Revoke this key?"
        message="Anything using it stops working immediately. This cannot be undone — issue a new key instead."
        confirmLabel="Revoke"
        loading={revoke.isPending}
      />
    </div>
  );
}
